import { Component, type ComponentChildren } from "preact";
import { Suspense, useEffect, useRef, useState } from "react";
import useSWR from "swr";
import { count, isoDay } from "../lib/format";
import { closeMarket, Link, useLoc } from "../lib/nav";
import { activeNavIndex, NAV, type NavItem } from "../lib/routes";
import { useApiStatus } from "../lib/status";
import { useWatchlist } from "../lib/watchlist";
import { ASK_ENABLED } from "../lib/flags";
import { SkeletonRows, StatePanel } from "./Bits";
import { PaletteButton } from "../views/CommandPalette";
import { MarketDrawer } from "./MarketDrawer";
import { ScrollRow } from "./ScrollRow";
import { useSourceHealth, viewSources } from "./sources";

function NavEntry({ item, active }: { item: NavItem; active: boolean }) {
	const [open, setOpen] = useState(false);
	const cls = `tab-t gap-1 ${active ? "is-on" : ""}`;
	if (!item.children) {
		return (
			<Link href={item.href} className={cls} aria-current={active ? "page" : undefined}>
				{item.label}
			</Link>
		);
	}
	return (
		<div
			className="relative"
			onMouseEnter={() => setOpen(true)}
			onMouseLeave={() => setOpen(false)}
			onFocusIn={() => setOpen(true)}
			onFocusOut={() => setOpen(false)}
			onKeyDown={(e: KeyboardEvent) => {
				if (e.key === "Escape") setOpen(false);
			}}
		>
			<Link href={item.href} className={cls} aria-current={active ? "page" : undefined} onClick={() => setOpen(false)}>
				{item.label}
				<span aria-hidden="true" className="text-label opacity-60">▾</span>
			</Link>
			{open ? (
				<div className="absolute top-full left-0 z-40 w-48 pt-1">
					<div className="border border-line-strong bg-panel py-1">
						{item.children.map((c) => (
							<Link
								key={c.href}
								href={c.href}
								onClick={() => setOpen(false)}
								className="block px-2.5 py-1.5 text-body text-fg hover:bg-raised hover:text-bright"
							>
								{c.label}
							</Link>
						))}
					</div>
				</div>
			) : null}
		</div>
	);
}

const WATCHLIST = "/watchlist";

function WatchlistLink({ path }: { path: string }) {
	const { items } = useWatchlist();
	const on = path === WATCHLIST;
	return (
		<Link href={WATCHLIST} aria-current={on ? "page" : undefined} className={`tab-t gap-1 ${on ? "is-on" : ""}`}>
			Watchlist
			{items.length ? <span className="num bg-up/15 px-1 text-label text-up">{items.length}</span> : null}
		</Link>
	);
}

function Header({ path }: { path: string }) {
	const currentHref = NAV[activeNavIndex(path)]?.href;
	const sections = NAV.filter((item) => item.href !== WATCHLIST);
	return (
		<header className="z-30 shrink-0 border-b border-line bg-ink">
			<div className="flex h-10 items-center justify-between gap-4 px-3 lg:px-5">
				<div className="flex min-w-0 items-center gap-5">
					<Link href="/" className="flex h-10 shrink-0 items-center gap-2" title="PolyGains home">
						<span aria-hidden="true" className="h-2 w-2 rotate-45 bg-up" />
						<span className="text-data font-bold tracking-[0.25em] text-up">POLYGAINS</span>
					</Link>
					<nav aria-label="Main" className="hidden items-center gap-0.5 lg:flex">
						{sections.map((item) => (
							<NavEntry key={item.href} item={item} active={item.href === currentHref} />
						))}
						{ASK_ENABLED ? <NavEntry item={{ label: "Ask", href: "/ask" }} active={path === "/ask"} /> : null}
					</nav>
				</div>
				<div className="flex shrink-0 items-center gap-3">
					<PaletteButton />
					<WatchlistLink path={path} />
					<a href="https://x.com/mevtools" target="_blank" rel="noreferrer" className="lnk-quiet flex h-10 items-center text-body text-mute">
						@mevtools
					</a>
				</div>
			</div>
			<div className="border-t border-line lg:hidden">
				<ScrollRow label="Main" active={currentHref} className="px-1">
					{sections.map((item) => (
						<Link
							key={item.href}
							href={item.href}
							aria-current={item.href === currentHref ? "page" : undefined}
							className={`tab-t shrink-0 grow justify-center ${item.href === currentHref ? "is-on" : ""}`}
						>
							{item.label}
						</Link>
					))}
				</ScrollRow>
			</div>
		</header>
	);
}

interface SegmentsStamp {
	generated_at?: string;
}

function SourceState({ name, state, title }: { name: string; state: "ok" | "down" | undefined; title?: string }) {
	return (
		<span title={title} className={state === "down" ? "text-dn" : state === "ok" ? "text-dim" : undefined}>
			{name} {state ?? "checking"}
		</span>
	);
}

/** Only the sources this view uses, so a working page does not look broken because another service is down. */
function StatusBar({ path }: { path: string }) {
	const api = useApiStatus();
	const health = useSourceHealth();
	// Reads the cache only: the app-wide fetcher would otherwise pull 1.1MB on every page for one date.
	const { data } = useSWR<SegmentsStamp>("data/segments.json", {
		revalidateIfStale: false,
		revalidateOnMount: false,
		revalidateOnReconnect: false,
	});
	const sources = viewSources(path);
	const items: ComponentChildren[] = [];
	for (const source of sources) {
		if (source === "gamma" || source === "data-api") {
			// Nothing asked of it yet (an empty watchlist, say) means nothing to report.
			if (health[source]) items.push(<SourceState key={source} name={source} state={health[source]} />);
		} else if (source === "polygains") {
			items.push(
				<SourceState
					key={source}
					name={source}
					state={api.state === "checking" ? undefined : api.state === "up" ? "ok" : "down"}
					title={api.latencyMs ? `Last health check took ${api.latencyMs}ms` : undefined}
				/>,
			);
		} else if (data?.generated_at) {
			items.push(<span key={source} className="num">wallet data as of {isoDay(data.generated_at)}</span>);
		}
	}
	return (
		<footer className="flex h-6 shrink-0 items-center gap-2 overflow-hidden border-t border-line bg-ink px-3 text-label whitespace-nowrap text-mute lg:px-5">
			{items.map((item, i) => (
				<span key={i} className="flex items-center gap-2">
					{i ? <span aria-hidden="true">·</span> : null}
					{item}
				</span>
			))}
		</footer>
	);
}

/** A view that throws should cost that view, not the whole app. */
class Boundary extends Component<{ children: ComponentChildren }, { failed: boolean }> {
	override state = { failed: false };
	static override getDerivedStateFromError() {
		return { failed: true };
	}
	override render() {
		if (!this.state.failed) return this.props.children;
		return (
			<div className="p-2">
				<StatePanel
					tag="ERROR"
					tone="dn"
					title="This view hit an error."
					action={
						<button type="button" className="ctl" onClick={() => window.location.reload()}>
							reload
						</button>
					}
				>
					<p>The rest of the site still works. Use the navigation above to move on.</p>
				</StatePanel>
			</div>
		);
	}
}

/** Stands in while a route's code loads; same frame as the views it precedes. */
function RouteSkeleton() {
	return (
		<div aria-hidden="true" className="mx-auto flex w-full max-w-[1440px] flex-col gap-2 px-3 py-2 lg:px-5">
			<div className="skeleton-bar h-5 w-48" />
			<div className="skeleton-bar h-3 w-3/5" />
			<div className="border border-line bg-panel">
				<SkeletonRows rows={14} cols={4} />
			</div>
		</div>
	);
}

const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const CLOSE_MS = 120;

/** Keeps the drawer mounted long enough to animate out. */
function useClosingDrawer(market: string | null) {
	const [shown, setShown] = useState(market);
	useEffect(() => {
		if (market) {
			setShown(market);
			return;
		}
		if (reducedMotion()) {
			setShown(null);
			return;
		}
		const t = window.setTimeout(() => setShown(null), CLOSE_MS);
		return () => window.clearTimeout(t);
	}, [market]);
	return { shown: market ?? shown, closing: !market && shown !== null };
}

export function Shell({ children }: { children: ComponentChildren }) {
	const { path, search } = useLoc();
	const market = new URLSearchParams(search).get("m");
	const drawer = useClosingDrawer(market);

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape" && new URLSearchParams(window.location.search).get("m")) {
				closeMarket();
				return;
			}
			const typing = (e.target as HTMLElement)?.closest?.("input, textarea, select, [contenteditable]");
			if (e.key === "/" && !typing && !e.metaKey && !e.ctrlKey) {
				const box = document.querySelector<HTMLInputElement>("input[data-search]");
				if (box) {
					e.preventDefault();
					box.focus();
				}
			}
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, []);

	return (
		<div className="flex h-dvh flex-col overflow-hidden bg-ink">
			<a href="#main-scroll" className="sr-only focus:not-sr-only focus:absolute focus:top-1 focus:left-1 focus:z-50 focus:bg-up focus:px-2 focus:py-1 focus:text-body focus:text-black">
				Skip to content
			</a>
			<Header path={path} />
			<main id="main-scroll" tabIndex={-1} className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto focus:outline-none">
				<Boundary key={path}>
					<Suspense fallback={<RouteSkeleton />}>{children}</Suspense>
				</Boundary>
			</main>
			<StatusBar path={path} />
			{drawer.shown ? (
				<Boundary key={drawer.shown}>
					<MarketDrawer conditionId={drawer.shown} closing={drawer.closing} />
				</Boundary>
			) : null}
		</div>
	);
}

/** Full-height frame for grid views: the grid scrolls, the page does not. */
export function View({ children }: { children: ComponentChildren }) {
	return <div className="mx-auto flex h-full w-full max-w-[1440px] flex-col gap-2 px-3 py-2 lg:px-5">{children}</div>;
}

/** Frame for pages that scroll as a whole: sections stack, the page scrolls. */
export function Page({ children }: { children: ComponentChildren }) {
	return <div className="mx-auto flex w-full max-w-[1440px] flex-col gap-2 px-3 py-2 lg:px-5">{children}</div>;
}
